import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  Button,
  Dialog,
  DialogActions,
  Alert,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";

const memberTypeOptions = [
  "freelancer",
  "partner_company",
  "internal_user",
  "vendor",
  "decorator",
  "designer",
  "developer",
  "logistics_partner",
];

function normalizeNetworkTypes(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

const emptyForm = {
  displayName: "",
  email: "",
  phone: "",
  companyName: "",
  profileImageUrl: "",
  memberType: "designer",
  networkTypesText: "",
  timezone: "",
  country: "",
  globalStatus: "active",
  useArtworkDesignerPreset: true,
  enablePortalAccess: true,
  sendPortalInvite: true,
  sourceLinkMode: "none",
  linkedDecoratorId: null,
};

export default function CoreNetworkMemberForm({
  open,
  onClose,
  onSubmit,
  initialValue = null,
  submitting = false,
  decoratorOptions = [],
}) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;
    if (initialValue) {
      setForm({
        displayName: initialValue.displayName || "",
        email: initialValue.email || "",
        phone: initialValue.phone || "",
        companyName: initialValue.companyName || "",
        profileImageUrl: initialValue.profileImageUrl || "",
        memberType: initialValue.memberType || "designer",
        networkTypesText: Array.isArray(initialValue.networkTypesJson)
          ? initialValue.networkTypesJson.join(", ")
          : "",
        timezone: initialValue.timezone || "",
        country: initialValue.country || "",
        globalStatus: initialValue.globalStatus || "active",
        useArtworkDesignerPreset: false,
        enablePortalAccess: initialValue.portalAccess?.portalAccessEnabled !== false,
        sendPortalInvite: false,
        sourceLinkMode: initialValue.sourceType === "decorator" && initialValue.sourceId ? "existing" : "none",
        linkedDecoratorId: initialValue.sourceType === "decorator" && initialValue.sourceId ? Number(initialValue.sourceId) : null,
      });
    } else {
      setForm(emptyForm);
    }
  }, [initialValue, open]);

  const setField = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async () => {
    await onSubmit?.({
      ...form,
      networkTypesJson: normalizeNetworkTypes(form.networkTypesText),
      sourceType: form.sourceLinkMode === "existing" ? "decorator" : null,
      sourceId: form.sourceLinkMode === "existing" && form.linkedDecoratorId ? form.linkedDecoratorId : null,
      email: String(form.email || "").trim().toLowerCase() || null,
    });
  };

  const selectedDecorator =
    decoratorOptions.find((option) => Number(option.id) === Number(form.linkedDecoratorId)) || null;
  const selectedDecoratorIsLocalOnly =
    form.sourceLinkMode === "existing" &&
    selectedDecorator &&
    selectedDecorator.isEliteDecorator !== true;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initialValue ? "Edit member" : "Add member"}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.25 }}>
          {!initialValue ? (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.useArtworkDesignerPreset}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          useArtworkDesignerPreset: event.target.checked,
                          memberType: event.target.checked ? "designer" : prev.memberType,
                          networkTypesText: event.target.checked ? "design" : prev.networkTypesText,
                          globalStatus: event.target.checked ? "active" : prev.globalStatus,
                          enablePortalAccess: event.target.checked ? true : prev.enablePortalAccess,
                          sendPortalInvite: event.target.checked ? true : prev.sendPortalInvite,
                        }))
                      }
                    />
                  }
                  label="Use Artwork Designer preset"
                />
              </Grid>
              {form.useArtworkDesignerPreset ? (
                <Grid item xs={12}>
                  <Alert severity="info">
                    This preset creates a global talent profile, connects it to the current instance, adds an active artwork-proof capability, and enables Talent Portal access.
                  </Alert>
                </Grid>
              ) : null}
            </>
          ) : null}
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Display name" value={form.displayName} onChange={setField("displayName")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Member type" value={form.memberType} onChange={setField("memberType")} disabled={form.useArtworkDesignerPreset}>
              {memberTypeOptions.map((option) => (
                <MenuItem value={option} key={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Email" value={form.email} onChange={setField("email")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Phone" value={form.phone} onChange={setField("phone")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Company" value={form.companyName} onChange={setField("companyName")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Profile Image URL"
              value={form.profileImageUrl}
              onChange={setField("profileImageUrl")}
              helperText="Used as the talent/provider logo or profile image across Core Network."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Status" value={form.globalStatus} onChange={setField("globalStatus")}>
              {["active", "inactive", "suspended", "invited"].map((option) => (
                <MenuItem value={option} key={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Network types"
              helperText="Comma-separated, for example: design, decoration"
              value={form.networkTypesText}
              onChange={setField("networkTypesText")}
              disabled={form.useArtworkDesignerPreset}
            />
          </Grid>
          {!initialValue ? (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.enablePortalAccess}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          enablePortalAccess: event.target.checked,
                          sendPortalInvite: event.target.checked ? prev.sendPortalInvite : false,
                        }))
                      }
                    />
                  }
                  label="Enable Talent Portal access"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.sendPortalInvite}
                      disabled={!form.enablePortalAccess}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          sendPortalInvite: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Send portal invitation now"
                />
              </Grid>
            </>
          ) : null}
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Timezone" value={form.timezone} onChange={setField("timezone")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Country" value={form.country} onChange={setField("country")} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Elite Decorator Link"
              value={form.sourceLinkMode}
              onChange={setField("sourceLinkMode")}
              helperText="Link this talent profile to an existing Elite Core Decorator to avoid duplicate vendor records."
            >
              <MenuItem value="none">No link yet</MenuItem>
              <MenuItem value="existing">Link existing Elite Core Decorator</MenuItem>
            </TextField>
          </Grid>
          {form.sourceLinkMode === "existing" ? (
            <Grid item xs={12}>
              <Autocomplete
                options={decoratorOptions}
                value={selectedDecorator}
                onChange={(_event, value) =>
                  setForm((prev) => ({
                    ...prev,
                    linkedDecoratorId: value?.id || null,
                  }))
                }
                getOptionLabel={(option) => option?.title || `Decorator ${option?.id || ""}`}
                isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Existing Elite Core Decorator"
                    helperText="Search and select the existing Decorator record to link."
                  />
                )}
              />
            </Grid>
          ) : null}
          {selectedDecoratorIsLocalOnly ? (
            <Grid item xs={12}>
              <Alert severity="warning">
                This decorator is currently local/private. Promote to Elite Decorator before using in Core Network.
              </Alert>
            </Grid>
          ) : null}
          {form.sourceLinkMode === "existing" && !decoratorOptions.length ? (
            <Grid item xs={12}>
              <Alert severity="info">No existing Decorator records were found for this instance yet.</Alert>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            submitting ||
            !form.displayName.trim() ||
            (form.sourceLinkMode === "existing" && !form.linkedDecoratorId)
          }
        >
          {submitting ? "Saving..." : initialValue ? "Save changes" : "Create member"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
